import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from '@mui/material';
import ProfilePasForm from '../../components/profilemng/ProfilePasForm';
import * as authApi from '../../lib/api/auth';
import { tempSetUser } from '../../modules/auth';

/**
 * 비밀번호 강제 변경 다이얼로그. bito 권한 체계 검토(2026-10-23) R1 - 신규 등록(초기
 * 비밀번호=이메일)/관리자 초기화 뒤에는 비밀번호를 한 번 바꾸기 전까지 화면을 쓸 수 없게
 * 막는다. 서버(UserDataDto.pwdCngReqYn, GET /api/v1/user/info)가 'Y'를 내려주는 동안
 * 로그인 상태의 모든 화면 위에 이 다이얼로그를 덮어씌운다(ESC/배경 클릭으로 닫을 수 없음).
 *
 * MainPage(= PrivateOutlet 안, 모든 보호된 라우트의 공통 뼈대)에 마운트한다 - 로그인
 * 상태가 아니면(auth.user가 없으면) 아무것도 렌더링하지 않는다.
 *
 * ProfiePassContainer(기존 "내 비밀번호 변경" 화면)과 같은 패턴(현재/변경/변경확인 3칸,
 * PUT /api/v1/user/changePwd)을 그대로 쓴다 - 서버가 현재 비밀번호를 다시 확인한다.
 */
const ForcedPasswordChangeContainer = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));

  const open = Boolean(user) && user.pwdCngReqYn === 'Y';

  const validationSchema = yup.object({
    currentPassword: yup.string('').required('현재 비밀번호를 입력하세요'),
    changedPassword: yup.string().required('변경할 비밀번호를 입력하세요'),
    changedConfirmPassword: yup
      .string()
      .required('변경 비밀번호 확인을 입력하세요')
      .test(
        'passwords-match',
        '변경비밀번호는 비밀번호 확인과 일치해야합니다.',
        function (value) {
          return this.parent.changedPassword === value;
        },
      ),
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      changedPassword: '',
      changedConfirmPassword: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      try {
        const res = await authApi.changePwd({
          currentPassword: values.currentPassword,
          changedPassword: values.changedPassword,
        });

        if (res.data?.result) {
          enqueueSnackbar('비밀번호를 변경했습니다.', { variant: 'success' });

          const nextUser = { ...user, pwdCngReqYn: 'N' };
          dispatch(tempSetUser(nextUser));
          try {
            localStorage.setItem('user', JSON.stringify(nextUser));
          } catch (e) {
            // localStorage 접근 불가(프라이빗 모드 등) - redux 상태는 이미 갱신됐으니 무시.
          }
          formik.resetForm();
        } else {
          enqueueSnackbar('현재 비밀번호가 올바르지 않습니다.', { variant: 'error' });
        }
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response.data.message
            : err.message;
        enqueueSnackbar(message, { variant: 'error' });
      }

      setSubmitting(false);
    },
  });

  if (!open) return null;

  return (
    <Dialog open={open} disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>비밀번호를 변경해야 합니다</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          신규 등록 또는 초기화된 비밀번호를 그대로 쓰고 있습니다. 계속 사용하려면
          먼저 비밀번호를 바꿔주세요.
        </DialogContentText>
        <ProfilePasForm formik={formik} />
      </DialogContent>
    </Dialog>
  );
};

export default ForcedPasswordChangeContainer;
